/* eslint-disable camelcase */
import type { CountryCode } from '@hotjar/country-lib';
import { keysToSnakeCase } from '@hotjar/insights-common/lib/convert-string';
import type { OrderRequestDto } from './dto';
import type {
  SubscriptionAmendmentRequest,
  SubscriptionCreationRequest,
  SubscriptionReactivationRequest,
  UpdateCustomerDetailsRequest,
} from './model';

export function mapSubscriptionCreationRequestModelToDto(
  model: SubscriptionCreationRequest,
): OrderRequestDto<'customer_and_subscription_creation_revised_order'> {
  const { customer, subscription } = keysToSnakeCase(model, true);
  const { billingAddress } = model.customer;
  const { billing_period, products } = subscription;

  return {
    customer_and_subscription_creation_revised_order: {
      customer: {
        ...customer,
        billing_address: {
          country: billingAddress?.country as CountryCode,
          address_line1: billingAddress?.addressLine1,
          address_line2: billingAddress?.addressLine2,
          email_address: billingAddress?.emailAddress as string,
          first_name: billingAddress?.firstName as string,
          last_name: billingAddress?.lastName as string,
          county: billingAddress?.county,
          city: billingAddress?.city,
          state: billingAddress?.state,
          zip_code: billingAddress?.zipCode,
        },
      },
      subscription: {
        billing_period,
        products: products.map(({ entity, sku }) => ({
          sku,
          entity: { ...entity, entity_identifier: entity.entity_identifier },
        })),
      },
    },
  };
}

export function mapSubscriptionReactivationRequestModelToDto(
  model: SubscriptionReactivationRequest,
): OrderRequestDto<'reactivate_subscription_order'> {
  const { subscription, hotjar_account_id } = keysToSnakeCase(model, true);
  const { billing_period, products } = subscription;

  return {
    reactivate_subscription_order: {
      hotjar_account_id,
      subscription: {
        billing_period,
        products: products.map(({ entity, sku }) => ({
          sku,
          entity: { ...entity, entity_identifier: entity.entity_identifier },
        })),
      },
    },
  };
}

export function mapSubscriptionAmendmentRequestModelToDto(
  model: SubscriptionAmendmentRequest,
): OrderRequestDto<'amend_subscription_order'> {
  const { products_to_add, products_to_remove, hotjar_account_id } = keysToSnakeCase(model, true);

  return {
    amend_subscription_order: {
      hotjar_account_id,
      products_to_add,
      products_to_remove,
    },
  };
}

export function mapUpdateCustomerDetailsRequestModelToDto(
  model: UpdateCustomerDetailsRequest,
): OrderRequestDto<'update_customer_details'> {
  const { hotjar_account_id, company_name, additional_email_addresses, tax_number } =
    keysToSnakeCase(model, true);

  const { billingAddress } = model;

  return {
    update_customer_details: {
      hotjar_account_id,
      ...(company_name !== undefined && { company_name }),
      // Because of a bug in the backend, we should always provide the billing address
      billing_address: {
        country: billingAddress?.country as CountryCode,
        address_line1: billingAddress?.addressLine1,
        address_line2: billingAddress?.addressLine2,
        email_address: billingAddress?.emailAddress ?? '',
        first_name: billingAddress?.firstName ?? '',
        last_name: billingAddress?.lastName ?? '',
        county: billingAddress?.county,
        city: billingAddress?.city,
        state: billingAddress?.state,
        zip_code: billingAddress?.zipCode,
      },
      ...(additional_email_addresses && { additional_email_addresses }),
      ...(tax_number !== undefined && { tax_number }),
    },
  };
}
